import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { groupUsersPageQuery, groupUsersRowsQuery, type GroupUsersRow } from '../../api/easeedeskDashboards'
import { Content } from '../../ui/Shell'
import { RANGES, rangeOf, type RangeName } from './range'
import { NotInstalled } from './WallHeader'

/**
 * "Dashboard Group" - `easeedesk-dashboards/Groups::group_users_tickets` +
 * `get_users_tickets`, macheta
 * `views/version-3/dashboards/easeedesk/group_users/index.php`.
 *
 * Cate un rand pe om: bulina de online, numele pe culoarea grupului lui,
 * tichetele pe fiecare tip (active si rezolvate, pe culoarea tipului), cate
 * are escaladate, cate conectate, cate de refacut, si cele doua punctaje.
 *
 * CASUTA DE GRUPURI CHIAR FILTREAZA ACUM. In portal, conditia se punea pe
 * `$this->db` iar interogarea pleca pe `$this->slavedb`, deci alegerea din
 * casuta nu ajungea niciodata in interogare.
 */

const STYLE = `
.od-group-users table { width: 100%; border-collapse: separate; border-spacing: 2px; }
.od-group-users th { background: #57636F; color: #fff; padding: 6px; text-align: center; font-size: 13px; }
.od-group-users td { padding: 6px; text-align: center; font-size: 14px; background: #fff; }
.od-group-users td.name { text-align: left; font-weight: bold; }
.od-group-users .dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 6px; }
.od-group-users .type { display: inline-block; color: #fff; border-radius: 3px; padding: 2px 6px; margin: 1px; font-size: 12px; }
`

function TypeChips({ row }: { row: GroupUsersRow }) {
  return (
    <>
      {row.tickets.map((ticket) => (
        <span
          key={ticket.ticket_type}
          className="type"
          style={{ backgroundColor: ticket.color_identifier || '#57636F' }}
          title={ticket.short_name}
        >
          {ticket.short_name} {ticket.active}/{ticket.resolved}
        </span>
      ))}
    </>
  )
}

/** Punctajele au zecimale numai cand chiar au (jumatatile de punct). */
function score(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

export function GroupUsersPage() {
  const [rangeName, setRangeName] = useState<RangeName>('Today')
  const [groups, setGroups] = useState<number[]>([])
  const page = useQuery(groupUsersPageQuery).data
  const rows = useQuery(groupUsersRowsQuery(groups, rangeOf(rangeName))).data
  const users = rows?.user_tickets ?? []

  return (
    <Content>
      <style>{STYLE}</style>
      <div className="od-group-users">
        <div className="row" style={{ marginBottom: 15 }}>
          <div className="col-md-6">
            <h4>{page?.title ?? 'Easeedesk :: Dashboard'}</h4>
          </div>
          <div className="col-md-3">
            <select
              multiple
              className="form-control"
              aria-label="Groups"
              value={groups.map(String)}
              onChange={(e) => setGroups(Array.from(e.target.selectedOptions, (o) => Number(o.value)))}
            >
              {(page?.groups ?? []).map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-control"
              aria-label="Date range"
              value={rangeName}
              onChange={(e) => setRangeName(e.target.value as RangeName)}
            >
              {RANGES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {page && !page.ready ? <NotInstalled what="Group dashboard" /> : null}

        <table id="group-users-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>User</th>
              <th>Tickets</th>
              <th>Active</th>
              <th>Resolved</th>
              <th>Escalated</th>
              <th>Connected</th>
              <th>Rework</th>
              <th>Score</th>
              <th>SLA score</th>
            </tr>
          </thead>
          <tbody>
            {users.map((row) => (
              <tr key={row.assignee} id={`group_user_${row.assignee}`}>
                <td className="name" style={{ borderLeft: `6px solid ${row.group_color || '#57636F'}` }}>
                  <span
                    className="dot"
                    style={{ backgroundColor: row.online ? '#0c834d' : '#c3c2b7' }}
                    title={row.online ? 'Online' : 'Offline'}
                  />
                  {row.username}
                </td>
                <td>
                  <TypeChips row={row} />
                </td>
                <td>{row.total_active}</td>
                <td>{row.total_resolved}</td>
                <td>{row.escalated}</td>
                <td>{row.connected}</td>
                <td>{row.rework}</td>
                <td>{score(row.total_score)}</td>
                <td>{score(row.sla_score)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Content>
  )
}
